import axios from 'axios';
import { SignInCredentials, AuthResponse } from '../types/auth';

import { API_URL as BASE_API_URL } from '../config';

const API_URL = `${BASE_API_URL}/auth`;

// Lazy import to avoid circular dependency with api.service.ts
const getApi = () => import('./api.service').then(m => m.default);

export const authService = {
  async signIn(credentials: SignInCredentials): Promise<AuthResponse> {
    const response = await axios.post<AuthResponse>(`${API_URL}/signin`, credentials);
    if (response.data.access_token) {
      localStorage.setItem('user', JSON.stringify(response.data));
    }
    return response.data;
  },

  logout(): void {
    localStorage.removeItem('user');
    localStorage.removeItem('selected_coloc');
  },

  getCurrentUser(): AuthResponse | null {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        localStorage.removeItem('user');
        return null;
      }
    }
    return null;
  },

  getToken(): string | null {
    const user = this.getCurrentUser();
    return user ? user.access_token : null;
  },

  isAuthenticated(): boolean {
    return !!this.getToken();
  },

  getUserRole(): string | null {
    const user = this.getCurrentUser();
    return user?.user?.role || null;
  },

  isOwner(): boolean {
    const role = this.getUserRole();
    return role === 'OWNER' || role === 'ADMIN';
  },

  isTenant(): boolean {
    return this.getUserRole() === 'TENANT';
  },

  isAgency(): boolean {
    return this.getUserRole() === 'AGENCY';
  },

  async forgotPassword(email: string): Promise<void> {
    await axios.post(`${API_URL}/forgot-password`, { email });
  },

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await axios.post(`${API_URL}/reset-password`, { token, new_password: newPassword });
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
    const api = await getApi();
    const response = await api.put('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },

  /** Valide l'adresse depuis le lien reçu par email. Aucune session requise :
   *  le destinataire ouvre souvent son courrier ailleurs que dans l'onglet
   *  où il est connecté. */
  async verifyEmail(token: string): Promise<void> {
    await axios.post(`${API_URL}/verify-email`, { token });
  },

  async resendVerification(): Promise<{ success: boolean; alreadyVerified: boolean }> {
    const api = await getApi();
    const response = await api.post('/auth/resend-verification');
    return response.data;
  },

  /**
   * DÉVELOPPEMENT : renvoie le lien de vérification au lieu de l'envoyer par
   * email. L'API répond 404 hors développement — la route ne révèle pas
   * qu'elle existe. Le jeton est le vrai, consommé par le parcours normal.
   */
  async devVerificationLink(): Promise<{ alreadyVerified: boolean; url: string | null }> {
    const api = await getApi();
    const response = await api.post('/auth/dev/verification-link');
    return response.data;
  },

  /** Recopie le statut vérifié dans l'utilisateur stocké, pour que l'interface
   *  cesse d'afficher « non vérifiée » sans imposer une reconnexion. */
  markEmailVerifiedLocally(): void {
    const stored = this.getCurrentUser();
    if (!stored?.user) return;
    localStorage.setItem(
      'user',
      JSON.stringify({ ...stored, user: { ...stored.user, emailVerified: true } }),
    );
  }
};
